/**
 * Job API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  BulkCVJobUpdateInput,
  CVJobUpdateInput,
  Job,
  JobAssignment,
  JobAssignmentBulkCreate,
  JobAssignmentCreate,
  JobAssignmentListResponse,
  JobCreateInput,
  JobCVCard,
  JobDetail,
  JobListResponse,
  JobUpdateInput,
} from '../types';

interface ListJobsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  // Filter params
  status?: string;
  positionIds?: string[];
  campaignIds?: string[];
  pipelineId?: string;
  search?: string;
  excludeCvId?: string;
}

interface JobResponse {
  success: boolean;
  data: Job;
  message: string;
}

interface JobListApiResponse {
  success: boolean;
  data: JobListResponse;
  message: string;
}

interface JobDetailApiResponse {
  success: boolean;
  data: JobDetail;
  message: string;
}

class JobService {
  /**
   * List Jobs with pagination and filters.
   * Converts page/size to skip/limit for backend API.
   */
  async listJobs(
    workspaceId: string,
    idToken: string,
    options: ListJobsOptions = {}
  ): Promise<JobListApiResponse> {
    const {
      page = 1,
      size = 10,
      sortBy = 'created_at',
      order = 'desc',
      status,
      positionIds,
      campaignIds,
      pipelineId,
      search,
      excludeCvId,
    } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JobListApiResponse>(`/jobs`, {
      params: {
        skip,
        limit: size,
        sort_by: sortBy,
        order,
        // Filter params (only include if defined)
        ...(status && { status }),
        // Multi-select: Axios converts arrays to repeated params
        ...(positionIds && positionIds.length > 0 && { position_id: positionIds }),
        ...(campaignIds && campaignIds.length > 0 && { campaign_id: campaignIds }),
        ...(pipelineId && { pipeline_id: pipelineId }),
        ...(search && { search }),
        ...(excludeCvId && { exclude_cv_id: excludeCvId }),
      },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list jobs');
    }

    return response.data;
  }

  /**
   * Get a single Job by ID.
   */
  async getJob(jobId: string, workspaceId: string, idToken: string): Promise<Job> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JobResponse>(`/jobs/${jobId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get job');
    }

    return response.data.data;
  }

  /**
   * Get Job with Kanban board data.
   */
  async getJobKanban(jobId: string, workspaceId: string, idToken: string): Promise<JobDetail> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JobDetailApiResponse>(`/jobs/${jobId}/kanban`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get job kanban');
    }

    return response.data.data;
  }

  /**
   * Create a new Job.
   */
  async createJob(workspaceId: string, idToken: string, data: JobCreateInput): Promise<Job> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<JobResponse>(`/jobs`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create job');
    }

    return response.data.data;
  }

  /**
   * Update a Job.
   * @param options.moveCvsOnPipelineChange If true and pipeline is changing,
   *        move all CVs to the first round of the new pipeline.
   */
  async updateJob(
    jobId: string,
    workspaceId: string,
    idToken: string,
    data: JobUpdateInput,
    options?: { moveCvsOnPipelineChange?: boolean }
  ): Promise<Job> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<JobResponse>(`/jobs/${jobId}`, data, {
      params: options?.moveCvsOnPipelineChange ? { move_cvs_on_pipeline_change: true } : undefined,
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update job');
    }

    return response.data.data;
  }

  /**
   * Delete a Job.
   */
  async deleteJob(jobId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/jobs/${jobId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }

  /**
   * Update a CV-Job link (move round, reject, set source, etc.).
   */
  async updateCVJob(
    jobId: string,
    cvId: string,
    workspaceId: string,
    idToken: string,
    data: CVJobUpdateInput
  ): Promise<{
    cv_id: string;
    job_id: string;
    round_id: string | null;
    is_rejected: boolean;
    rejection_reason_id: string | null;
    rejection_reason_text: string | null;
    source_id: string | null;
    applied_at: string | null;
    onboard_date: string | null;
  }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<{
      success: boolean;
      data: {
        cv_id: string;
        job_id: string;
        round_id: string | null;
        is_rejected: boolean;
        rejection_reason_id: string | null;
        rejection_reason_text: string | null;
        source_id: string | null;
        applied_at: string | null;
        onboard_date: string | null;
      };
      message: string;
    }>(`/jobs/${jobId}/cvs/${cvId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update CV-Job');
    }

    return response.data.data;
  }

  /**
   * Move a CV to a different pipeline round.
   * @deprecated Use updateCVJob instead
   */
  async updateCVRound(
    jobId: string,
    cvId: string,
    workspaceId: string,
    idToken: string,
    roundId: string
  ): Promise<{ cv_id: string; job_id: string; round_id: string | null }> {
    const result = await this.updateCVJob(jobId, cvId, workspaceId, idToken, {
      round_id: roundId,
    });
    return {
      cv_id: result.cv_id,
      job_id: result.job_id,
      round_id: result.round_id,
    };
  }

  /**
   * Bulk update multiple CV-Job links.
   */
  async bulkUpdateCVJobs(
    jobId: string,
    workspaceId: string,
    idToken: string,
    data: BulkCVJobUpdateInput
  ): Promise<{ updated_count: number }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<{
      success: boolean;
      data: { updated_count: number };
      message: string;
    }>(`/jobs/${jobId}/cvs/bulk`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk update CV-Jobs');
    }

    return response.data.data;
  }

  /**
   * Upload CV file to a job.
   * Creates CV, links to job at first pipeline round, and triggers parsing.
   * @param poolIds Optional array of pool IDs to add the CV to those pools
   */
  async uploadCVToJob(
    jobId: string,
    workspaceId: string,
    idToken: string,
    file: File,
    poolIds?: string[],
    sourceId?: string,
    picId?: string,
    referrerId?: string
  ): Promise<JobCVCard> {
    const client = createApiClient(idToken, 'ats');
    const formData = new FormData();
    formData.append('file', file);

    // Add pool_ids as form data if provided
    if (poolIds && poolIds.length > 0) {
      poolIds.forEach((poolId) => {
        formData.append('pool_ids', poolId);
      });
    }

    // Add optional fields
    if (sourceId) {
      formData.append('source_id', sourceId);
    }
    if (picId) {
      formData.append('pic_id', picId);
    }
    if (referrerId) {
      formData.append('referrer_id', referrerId);
    }

    const response = await client.post<{
      success: boolean;
      data: JobCVCard;
      message: string;
    }>(`/jobs/${jobId}/upload-cv`, formData, {
      headers: {
        'X-Workspace-ID': workspaceId,
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to upload CV');
    }

    return response.data.data;
  }

  /**
   * Add existing CVs to a job.
   * Creates CV-Job links at the first pipeline round.
   */
  async addExistingCVsToJob(
    jobId: string,
    workspaceId: string,
    idToken: string,
    cvIds: string[]
  ): Promise<{
    added_count: number;
    skipped_count: number;
    cv_ids: string[];
    skipped: Array<{ cv_id: string; reason: string }> | null;
    round_id: string;
  }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      data: {
        added_count: number;
        skipped_count: number;
        cv_ids: string[];
        skipped: Array<{ cv_id: string; reason: string }> | null;
        round_id: string;
      };
      message: string;
    }>(
      `/jobs/${jobId}/cvs/add`,
      { cv_ids: cvIds },
      {
        headers: { 'X-Workspace-ID': workspaceId },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to add CVs to job');
    }

    return response.data.data;
  }

  /**
   * List all assignments for a job.
   */
  async listJobAssignments(
    jobId: string,
    workspaceId: string,
    idToken: string
  ): Promise<JobAssignmentListResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<{
      success: boolean;
      data: JobAssignmentListResponse;
      message: string;
    }>(`/jobs/${jobId}/assignments`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list job assignments');
    }

    return response.data.data;
  }

  /**
   * Create a single job assignment.
   */
  async createJobAssignment(
    jobId: string,
    workspaceId: string,
    idToken: string,
    data: JobAssignmentCreate
  ): Promise<JobAssignment> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      data: JobAssignment;
      message: string;
    }>(`/jobs/${jobId}/assignments`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create job assignment');
    }

    return response.data.data;
  }

  /**
   * Create multiple job assignments in bulk.
   */
  async createJobAssignmentsBulk(
    jobId: string,
    workspaceId: string,
    idToken: string,
    data: JobAssignmentBulkCreate
  ): Promise<JobAssignmentListResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      data: JobAssignmentListResponse;
      message: string;
    }>(`/jobs/${jobId}/assignments/bulk`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create job assignments');
    }

    return response.data.data;
  }

  /**
   * Delete a job assignment.
   */
  async deleteJobAssignment(
    jobId: string,
    assigneeId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/jobs/${jobId}/assignments/${assigneeId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const jobService = new JobService();
export default jobService;
